import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DatePickerComponent } from '@app/components/date-picker/date-picker.component';
import { TagSelectorComponent } from '@app/components/tag-selector/tag-selector.component';
import { MsfInput } from '@components/ui/forms/input/input';
import { MsfTreeSelect } from '@components/tree-select/tree-select';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { UploadedGeoImagesStateService } from '../services/uploaded-geo-images-state.service';
import { GeoImageFilters, UploadedGeoImageRegionNode, hasActiveGeoImageFilters } from '../services/uploaded-geo-image.models';

interface RegionOption {
  id: string;
  name: string;
  parentId: string | null;
}

/**
 * Filter panel of the "Uploaded Geo-Images" tab (FR-011): region, tags, capture-date range, and name search.
 * There is deliberately no cloud-cover filter (meaningless for uploaded images) and no data-source filter
 * (being on this tab already is the data-source choice).
 */
@Component({
  selector: 'app-geo-image-filters',
  standalone: true,
  templateUrl: './geo-image-filters.component.html',
  styleUrls: ['./geo-image-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TranslateModule, DatePickerComponent, TagSelectorComponent, MsfInput, MsfTreeSelect, PrimaryButton, SecondaryButton]
})
export class GeoImageFiltersComponent {
  /** The region tree the server returns is nested; the tree-select wants it flat with a parent id. */
  readonly regionOptions = computed<RegionOption[]>(() => {
    const flat: RegionOption[] = [];
    const visit = (nodes: UploadedGeoImageRegionNode[]): void => {
      for (const node of nodes) {
        flat.push({ id: node.id, name: node.name, parentId: node.parentId });
        visit(node.children ?? []);
      }
    };
    visit(this.state.metadata()?.regions ?? []);
    return flat;
  });

  /** Reset is only useful once a filter is applied or the panel was edited. */
  readonly canReset = computed(() => hasActiveGeoImageFilters(this.state.filters()) || hasActiveGeoImageFilters(this.state.appliedFilters()));

  constructor(public readonly state: UploadedGeoImagesStateService) {}

  /** Every edit to the draft filters goes through here, so the signal always gets a fresh object. */
  patchFilters(patch: Partial<GeoImageFilters>): void {
    this.state.filters.update(current => ({ ...current, ...patch }));
  }

  onRegionChange(regionId: string | null): void {
    this.patchFilters({ regionId: regionId || null });
  }

  onTagIdsChange(tagIds: string[]): void {
    this.patchFilters({ tagIds: [...tagIds] });
  }

  apply(): void {
    this.state.applyFilters();
  }

  reset(): void {
    this.state.resetFilters();
  }
}
